import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Launch-offer bar with a live countdown.
//
// It renders nothing at all unless the server says an offer is running: the
// end date lives in backend/services/pricing.py and arrives on every
// /api/pricing row as `launch_ends_at`. Nothing needs deploying to take this
// down on October 1st, it simply stops appearing.
//
// The countdown is deliberately days/hours/minutes and not seconds. A ticking
// seconds digit re-renders the whole bar once a second for no persuasive gain,
// and it reads as a pressure tactic rather than a deadline.

function remaining(endsAt, now) {
  const ms = new Date(endsAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
  };
}

export default function LaunchBanner({ endsAt, originalDisplay, priceDisplay }) {
  const { t } = useTranslation("landing");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, [endsAt]);

  // The site nav is sticky at top: 0 too, so without this it would sit on top
  // of the bar. Publishing the measured height lets the nav offset itself
  // (App.css reads --launch-banner-h), which also survives the bar wrapping
  // onto two lines on a narrow screen. Cleared on unmount so the nav returns
  // to the very top the moment the offer ends.
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty("--launch-banner-h", "0px");
      return undefined;
    }
    const publish = () =>
      root.style.setProperty("--launch-banner-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--launch-banner-h", "0px");
    };
  });

  if (!endsAt || !originalDisplay || !priceDisplay) return null;
  const left = remaining(endsAt, now);
  // Past the deadline the server has already stopped discounting, so the bar
  // must not keep promising one while the page shows the regular price.
  if (!left) return null;

  const parts = [
    left.days ? `${left.days}d` : null,
    left.days || left.hours ? `${left.hours}h` : null,
    `${left.minutes}m`,
  ].filter(Boolean);

  return (
    <div className="launch-banner" role="status" ref={ref}>
      <span className="launch-banner-tag">{t("launch.tag")}</span>
      <span className="launch-banner-price">
        <s>{originalDisplay}</s> <strong>{priceDisplay}</strong>
      </span>
      <span className="launch-banner-time">{t("launch.ends")} {parts.join(" ")}</span>
    </div>
  );
}
